import React, { useEffect, useState, useRef } from 'react';
import { Sparkles, Gift, Heart, X, Plus, Zap } from 'lucide-react';
interface GenerationUpsellCTAProps {
  compact?: boolean;
  onClose?: () => void;
}
export const GenerationUpsellCTA: React.FC<GenerationUpsellCTAProps> = ({
  compact = false,
  onClose
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [arrowPath, setArrowPath] = useState({
    start: {
      x: 0,
      y: 0
    },
    end: {
      x: 0,
      y: 0
    }
  });
  const bannerRef = useRef<HTMLDivElement>(null);
  // Calculate the position of the floating action button
  useEffect(() => {
    if (!isHovered) return;
    const calculatePositions = () => {
      // Get banner position
      if (!bannerRef.current) return;
      const bannerRect = bannerRef.current.getBoundingClientRect();
      const bannerBottomRight = {
        x: bannerRect.right,
        y: bannerRect.bottom
      };
      // Floating action button is fixed at bottom right
      const fabPosition = {
        x: window.innerWidth - 40,
        y: window.innerHeight - 40 // Approximate center of FAB
      };
      setArrowPath({
        start: bannerBottomRight,
        end: fabPosition
      });
    };
    // Calculate initially and on resize or scroll
    calculatePositions();
    window.addEventListener('resize', calculatePositions);
    window.addEventListener('scroll', calculatePositions);
    return () => {
      window.removeEventListener('resize', calculatePositions);
      window.removeEventListener('scroll', calculatePositions);
    };
  }, [isHovered]);
  return <div className="w-full relative" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-7920">
      <div ref={bannerRef} className={`w-full ${compact ? '' : 'max-w-2xl mx-auto'}`} data-id="element-7921">
        <div className="relative bg-gradient-to-r from-pink-600/90 to-purple-600/90 rounded-xl overflow-hidden animate-pulse-slow" data-id="element-7922">
          {/* Background sparkle effects */}
          <div className="absolute inset-0 opacity-20" data-id="element-7923">
            <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white rounded-full animate-ping" style={{
            animationDuration: '2s'
          }} data-id="element-7924"></div>
            <div className="absolute top-3/4 left-1/3 w-2 h-2 bg-white rounded-full animate-ping" style={{
            animationDuration: '3s'
          }} data-id="element-7925"></div>
            <div className="absolute top-1/3 right-1/4 w-2 h-2 bg-white rounded-full animate-ping" style={{
            animationDuration: '2.5s'
          }} data-id="element-7926"></div>
            <div className="absolute bottom-1/4 right-1/3 w-2 h-2 bg-white rounded-full animate-ping" style={{
            animationDuration: '1.5s'
          }} data-id="element-7927"></div>
          </div>
          {/* Content */}
          <div className="relative p-4 md:p-6" data-id="element-7928">
            {onClose && <button onClick={onClose} className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white" data-id="element-7929">
                <X size={14} data-id="element-7930" />
              </button>}
            <div className={`flex ${compact ? 'flex-row items-center' : 'flex-col md:flex-row items-start md:items-center'} gap-4`} data-id="element-7931">
              {/* Icon */}
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center" data-id="element-7932">
                <Plus size={24} className="text-white" data-id="element-7933" />
              </div>
              {/* Text content */}
              <div className={`flex-1 ${compact ? 'space-y-0.5' : 'space-y-2'}`} data-id="element-7934">
                <h3 className={`font-bold text-white ${compact ? 'text-base' : 'text-xl'}`} data-id="element-7935">
                  Try the Floating Action Button!
                </h3>
                {!compact && <p className="text-white/80 text-sm" data-id="element-7936">
                    Click the + button in the bottom right corner and get{' '}
                    <span className="font-bold" data-id="element-7937">100 free LuvTokens</span> on
                    your first use!
                  </p>}
                <div className="flex flex-wrap gap-2 mt-2" data-id="element-7938">
                  <div className="flex items-center gap-1 px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white" data-id="element-7939">
                    <Gift size={12} data-id="element-7940" />
                    <span data-id="element-7941">Free Tokens</span>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white" data-id="element-7942">
                    <Heart size={12} data-id="element-7943" />
                    <span data-id="element-7944">Special Rewards</span>
                  </div>
                  <div className="flex items-center gap-1 px-2 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs text-white" data-id="element-7945">
                    <Zap size={12} data-id="element-7946" />
                    <span data-id="element-7947">Exclusive Content</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Fancy Arrow - Fixed position overlay that appears on hover */}
      {isHovered && <div className="fixed inset-0 pointer-events-none z-50" data-id="element-7948">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" data-id="element-7949">
            <defs data-id="element-7950">
              <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="100%" data-id="element-7951">
                <stop offset="0%" stopColor="rgba(236, 72, 153, 0.9)" data-id="element-7952" />
                <stop offset="100%" stopColor="rgba(147, 51, 234, 0.9)" data-id="element-7953" />
              </linearGradient>
              <filter id="glow" x="-20%" y="-20%" width="140%" height="140%" data-id="element-7954">
                <feGaussianBlur stdDeviation="4" result="blur" data-id="element-7955" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" data-id="element-7956" />
              </filter>
            </defs>
            {/* Curved path from banner to FAB */}
            <path d={`M ${arrowPath.start.x} ${arrowPath.start.y} 
                  Q ${arrowPath.start.x + 50} ${arrowPath.end.y - 50}, 
                  ${arrowPath.end.x - 30} ${arrowPath.end.y - 30}`} fill="none" stroke="url(#arrowGradient)" strokeWidth="3" strokeDasharray="8 4" strokeLinecap="round" filter="url(#glow)" className="animate-pulse-slow" data-id="element-7957" />
            {/* Arrow head */}
            <polygon points={`
                ${arrowPath.end.x - 30},${arrowPath.end.y - 30}
                ${arrowPath.end.x - 40},${arrowPath.end.y - 40}
                ${arrowPath.end.x - 15},${arrowPath.end.y - 15}
              `} fill="url(#arrowGradient)" filter="url(#glow)" className="animate-pulse-slow" data-id="element-7958" />
            {/* Pulsing circle at the end (near FAB) */}
            <circle cx={arrowPath.end.x - 15} cy={arrowPath.end.y - 15} r="8" fill="rgba(236, 72, 153, 0.7)" className="animate-ping" style={{
          animationDuration: '1.5s'
        }} data-id="element-7959" />
          </svg>
        </div>}
      <style jsx data-id="element-7960">{`
        @keyframes pulse-slow {
          0%,
          100% {
            opacity: 0.7;
            filter: drop-shadow(0 0 5px rgba(236, 72, 153, 0.5));
          }
          50% {
            opacity: 1;
            filter: drop-shadow(0 0 10px rgba(236, 72, 153, 0.8));
          }
        }
        .animate-pulse-slow {
          animation: pulse-slow 3s infinite;
        }
      `}</style>
    </div>;
};